import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import ImageUploader from './components/ImageUploader';
import ImageGallery from './components/ImageGallery';
import ParticlesBackground from './components/ParticlesBackground';
import SmoothScroll from './components/SmoothScroll';

// Pages
import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import SubscriptionPage from './components/SubscriptionPage';
import SignupSubscribePage from './components/SignupSubscribePage';
import AboutPage from './components/AboutPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import axios from 'axios';

import TexturePanel from './components/TexturePanel';
import EnvironmentPanel from './components/EnvironmentPanel';
import SidebarMenu from './components/SidebarMenu';

const TEXTURE_COLORS = [
  '#22c55e',
  '#1f2937',
  '#ef4444',
  '#3b82f6',
  '#eab308',
  '#a855f7',
  '#ec4899',
  '#f97316',
  '#06b6d4',
  '#6366f1',
  '#6366f1',
];

// Configure Axios for CSRF
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';
axios.defaults.withCredentials = true;

// ─── AppShell: The authenticated app interface ───────────────────────────────
function AppShell({ userData, onLogout, isMenuOpen, setIsMenuOpen }) {
  const [refreshGallery, setRefreshGallery] = useState(0);
  const [environmentImage, setEnvironmentImage] = useState(null);
  const [styleImage, setStyleImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [customTextures, setCustomTextures] = useState([
    { id: 'default-1', name: 'Pelouse', color: TEXTURE_COLORS[0], file: null, preview: null },
    { id: 'default-2', name: 'Enrobé', color: TEXTURE_COLORS[1], file: null, preview: null },
  ]);
  const [showTextureDots, setShowTextureDots] = useState(true);
  const [mode, setMode] = useState('upload');

  const addCustomTexture = (file) => {
    if (customTextures.length >= 10) {
      alert("Limite de 10 textures atteinte.");
      return;
    }
    const usedColors = customTextures.map(t => t.color);
    const availableColor = TEXTURE_COLORS.find(c => !usedColors.includes(c));
    if (!availableColor) {
      alert("Plus de couleurs disponibles.");
      return;
    }
    const newTexture = {
      id: `custom-${Date.now()}`,
      name: file.name.substring(0, 12),
      color: availableColor,
      file: file,
      preview: URL.createObjectURL(file)
    };
    setCustomTextures([...customTextures, newTexture]);
    setSelectedTexture(newTexture);
  };

  return (
    <div className="App">
      <SidebarMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onLogout={onLogout}
        userData={userData}
      />

      <ParticlesBackground active={isGenerating} />

      <TexturePanel
        textures={customTextures}
        selectedTexture={selectedTexture}
        onSelectTexture={setSelectedTexture}
        onAddTexture={addCustomTexture}
        showDots={showTextureDots}
        onToggleDots={() => setShowTextureDots(!showTextureDots)}
      />
      <EnvironmentPanel
        environmentImage={environmentImage}
        styleImage={styleImage}
        onEnvironmentSelect={setEnvironmentImage}
        onStyleSelect={setStyleImage}
      />

      <header className="App-header">
        <button
          className="app-hamburger-btn"
          onClick={() => setIsMenuOpen(true)}
        >
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </button>

        <div className="header-title-container">
          <h1>ARCHIVIZAI</h1>
        </div>

        <div className="mode-switcher header-tabs">
          <span className={`mode-link ${mode === 'draw' ? 'active' : ''}`} onClick={() => setMode('draw')}>DESSIN</span>
          <span className="separator">/</span>
          <span className={`mode-link ${mode === 'upload' ? 'active' : ''}`} onClick={() => setMode('upload')}>IMAGE</span>
          <span className="separator">/</span>
          <span className={`mode-link ${mode === 'model3d' ? 'active' : ''}`} onClick={() => setMode('model3d')}>MODEL 3D</span>
        </div>
      </header>

      <main className="App-main">
        <ImageUploader
          mode={mode}
          setMode={setMode}
          onImageGenerated={() => setRefreshGallery(prev => prev + 1)}
          environmentImage={environmentImage}
          styleImage={styleImage}
          onGenerationStart={() => setIsGenerating(true)}
          onGenerationEnd={() => setIsGenerating(false)}
          selectedTexture={selectedTexture}
          showTextureDots={showTextureDots}
          userData={userData}
        />
        <ImageGallery key={refreshGallery} />
      </main>
    </div>
  );
}

// ─── Root App: Handles auth state + routing ───────────────────────────────────
function App() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Prime CSRF once
  useEffect(() => {
    const primeCSRF = async () => {
      try {
        await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/set-csrf/`);
      } catch (e) {
        console.log("CSRF Priming failed", e);
      }
    };
    primeCSRF();
  }, []);

  // Check if user is already logged in (session cookie)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/dj-rest-auth/user/`,
          { withCredentials: true }
        );
        setUserData(response.data);
        setIsAuthenticated(true);
      } catch (e) {
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/dj-rest-auth/user/`,
        { withCredentials: true }
      );
      setUserData(response.data);
    } catch (e) {
      console.error("Failed to fetch user data", e);
    }
  }, []);

  // Poll user data when authenticated
  useEffect(() => {
    let intervalId;
    if (isAuthenticated) {
      fetchUserData();
      intervalId = setInterval(fetchUserData, 10000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [isAuthenticated, fetchUserData]);

  const handleLogin = useCallback(() => {
    setIsAuthenticated(true);
    fetchUserData();
    navigate('/app');
  }, [navigate, fetchUserData]);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/dj-rest-auth/logout/`);
    } catch (e) {
      console.warn("Logout server error or already logged out", e);
    } finally {
      setUserData(null);
      setIsAuthenticated(false);
      setIsMenuOpen(false);
      navigate('/');
    }
  }, [navigate]);

  // Auto token refresh interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/token/refresh')) {
            return Promise.reject(error);
          }
          originalRequest._retry = true;
          try {
            await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/dj-rest-auth/token/refresh/`);
            return axios(originalRequest);
          } catch (refreshError) {
            handleLogout();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => { axios.interceptors.response.eject(interceptor); };
  }, [handleLogout]);

  // Don't render routes until we know if user is authenticated
  if (!authChecked) {
    return null;
  }

  return (
    <SmoothScroll>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/a-propos" element={<AboutPage />} />
        <Route path="/inscription" element={<SignupSubscribePage />} />
        <Route path="/abonnement" element={<SubscriptionPage onSubscribe={handleLogin} />} />
        <Route path="/connexion" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
        <Route path="/password-reset/confirm/:uid/:token" element={<ResetPasswordPage />} />

        {/* Protected route */}
        <Route
          path="/app"
          element={
            isAuthenticated
              ? <AppShell userData={userData} onLogout={handleLogout} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
              : <Navigate to="/connexion" replace />
          }
        />

        {/* Catch-all fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SmoothScroll>
  );
}

export default App;
